import { Canvas, useLoader, extend, useThree } from "@react-three/fiber";
import { CameraControls } from "@react-three/drei";
import { useErrorBoundary } from "use-error-boundary";
import React, { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import {
  buildRoomModulesZipName,
  exportRoomModulesAsGlbZip,
} from "./utils/exportGlbModules";

// Extend Three.js with the necessary components
extend({
  LineSegments: THREE.LineSegments,
  EdgesGeometry: THREE.EdgesGeometry,
  LineBasicMaterial: THREE.LineBasicMaterial,
});

// 1×1 transparent PNG — useLoader must run unconditionally; unused loads map to no laminate.
const LAMINATE_TEXTURE_PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";

function ThreeDCanvas({
  currentRoom,
  showShutters,
  showRemovedShutters,
  showDrilling,
  showEdges,
  mode,
  selectionManager,
}) {
  const { ErrorBoundary, didCatch, error } = useErrorBoundary();

  function setLaminateProperties(
    panel,
    laminateTexture,
    identifier,
    imageHeight,
    imageWidth
  ) {
    laminateTexture = laminateTexture.clone();
    const laminateHeight = imageHeight || 2420;
    const laminateWidth = imageWidth || 1220;
    laminateTexture.wrapS = THREE.RepeatWrapping;
    laminateTexture.wrapT = THREE.RepeatWrapping;
    if (panel.rotated) {
      laminateTexture.rotation = (90 * Math.PI) / 180;
    }
    // FIXME: Side variable in conjunction with orientation should be used to calculate the texture repeat
    if (panel.orientation == "surface") {
      if (panel.axes?.["x"] == "length_1")
        laminateTexture.rotation = (90 * Math.PI) / 180;
      laminateTexture.repeat.set(
        panel.dimensions.width / laminateWidth,
        panel.dimensions.height / laminateHeight
      );
    }
    if (panel.orientation == "horizontal") {
      if (panel.axes?.["x"] == "length_1")
        laminateTexture.rotation = (90 * Math.PI) / 180;
      laminateTexture.repeat.set(
        panel.dimensions.width / laminateWidth,
        panel.dimensions.depth / laminateHeight
      );
    }
    if (panel.orientation == "vertical") {
      if (panel.axes?.["z"] == "length_1")
        laminateTexture.rotation = (90 * Math.PI) / 180;
      laminateTexture.repeat.set(
        panel.dimensions.depth / laminateWidth,
        panel.dimensions.height / laminateHeight
      );
    }
    return laminateTexture;
  }

  function LaminateMaterial(props) {
    const panel = props.panel;

    const getLaminateBaseName = (laminate, fallbackName) => {
      const imageUrl = laminate?.image_url;
      if (!imageUrl) return fallbackName;
      const filename = imageUrl.split("?")[0].split("/").pop() || "";
      const baseName = filename.replace(/\.[^/.]+$/, "");
      return baseName || fallbackName;
    };

    const frontName = getLaminateBaseName(panel.front_laminate, "front");
    const backName = getLaminateBaseName(panel.back_laminate, frontName);
    const topName = getLaminateBaseName(panel.top_laminate, frontName);
    const bottomName = getLaminateBaseName(panel.bottom_laminate, frontName);
    const leftName = getLaminateBaseName(panel.left_laminate, frontName);
    const rightName = getLaminateBaseName(panel.right_laminate, frontName);

    const frontUrl = panel.front_laminate?.image_url
      ? `${panel.front_laminate.image_url}?3d=true`
      : LAMINATE_TEXTURE_PLACEHOLDER;
    const frontLoaded = useLoader(THREE.TextureLoader, frontUrl);
    let frontLaminateTexture = panel.front_laminate
      ? setLaminateProperties(
          panel,
          frontLoaded.clone(),
          "front",
          panel.front_laminate.image_height,
          panel.front_laminate.image_width
        )
      : null;

    const backUrl = panel.back_laminate?.image_url
      ? `${panel.back_laminate.image_url}?3d=true`
      : LAMINATE_TEXTURE_PLACEHOLDER;
    const backLoaded = useLoader(THREE.TextureLoader, backUrl);
    let backLaminateTexture = panel.back_laminate
      ? setLaminateProperties(
          panel,
          backLoaded.clone(),
          "back",
          panel.back_laminate.image_height,
          panel.back_laminate.image_width
        )
      : frontLaminateTexture;

    const topUrl = panel.top_laminate?.image_url
      ? `${panel.top_laminate.image_url}?3d=true`
      : LAMINATE_TEXTURE_PLACEHOLDER;
    const topLoaded = useLoader(THREE.TextureLoader, topUrl);
    let topLaminateTexture = panel.top_laminate
      ? setLaminateProperties(
          panel,
          topLoaded.clone(),
          "top",
          panel.top_laminate.image_height,
          panel.top_laminate.image_width
        )
      : frontLaminateTexture;

    const bottomUrl = panel.bottom_laminate?.image_url
      ? `${panel.bottom_laminate.image_url}?3d=true`
      : LAMINATE_TEXTURE_PLACEHOLDER;
    const bottomLoaded = useLoader(THREE.TextureLoader, bottomUrl);
    let bottomLaminateTexture = panel.bottom_laminate
      ? setLaminateProperties(
          panel,
          bottomLoaded.clone(),
          "bottom",
          panel.bottom_laminate.image_height,
          panel.bottom_laminate.image_width
        )
      : frontLaminateTexture;

    const leftUrl = panel.left_laminate?.image_url
      ? `${panel.left_laminate.image_url}?3d=true`
      : LAMINATE_TEXTURE_PLACEHOLDER;
    const leftLoaded = useLoader(THREE.TextureLoader, leftUrl);
    let leftLaminateTexture = panel.left_laminate
      ? setLaminateProperties(
          panel,
          leftLoaded.clone(),
          "left",
          panel.left_laminate.image_height,
          panel.left_laminate.image_width
        )
      : frontLaminateTexture;

    const rightUrl = panel.right_laminate?.image_url
      ? `${panel.right_laminate.image_url}?3d=true`
      : LAMINATE_TEXTURE_PLACEHOLDER;
    const rightLoaded = useLoader(THREE.TextureLoader, rightUrl);
    let rightLaminateTexture = panel.right_laminate
      ? setLaminateProperties(
          panel,
          rightLoaded.clone(),
          "right",
          panel.right_laminate.image_height,
          panel.right_laminate.image_width
        )
      : frontLaminateTexture;

    return [
      <meshStandardMaterial
        name={rightName}
        map={rightLaminateTexture}
        attach="material-0" //right
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        name={leftName}
        map={leftLaminateTexture}
        attach="material-1" //left
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        name={topName}
        map={topLaminateTexture}
        attach="material-2" //top
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        name={bottomName}
        map={bottomLaminateTexture}
        attach="material-3" //bottom
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        name={frontName}
        map={frontLaminateTexture}
        attach="material-4" //front
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
      <meshStandardMaterial
        name={backName}
        map={backLaminateTexture}
        attach="material-5" //back
        roughness={0.4}
        metalness={0.2}
        attachArray="material"
        transparent={showDrilling}
        opacity={showDrilling ? 0.6 : 1}
      />,
    ];
  }

  function getRotation(drilling) {
    let rotation = [0, 0, 0];

    if (drilling.side == "left") {
      rotation = [0, 0, Math.PI / 2];
    }
    if (drilling.side == "right") {
      rotation = [0, 0, -Math.PI / 2];
    }
    if (drilling.side == "front") {
      rotation = [Math.PI / 2, 0, 0];
    }
    if (drilling.side == "back") {
      rotation = [-Math.PI / 2, 0, 0];
    }
    if (drilling.side == "bottom") {
      rotation = [-Math.PI, 0, 0];
    }
    if (drilling.side == "top") {
      rotation = [0, 0, 0];
    }
    return rotation;
  }

  function getPosition(drilling, panel) {
    let position = drilling.position;
    if (drilling.type == "surface_hole") {
      if (drilling.side == "left") {
        position.x = 0;
      }
      if (drilling.side == "right") {
        position.x = panel.dimensions.width;
      }
      if (drilling.side == "front") {
        position.z = panel.dimensions.depth;
      }
      if (drilling.side == "back") {
        position.z = 0;
      }
      if (drilling.side == "bottom") {
        position.y = 0;
      }
      if (drilling.side == "top") {
        position.y = panel.dimensions.height;
      }
    }
    return position;
  }

  function getDrillingColor(type) {
    if (type == "dowel") {
      return "blue";
    }
    if (type == "minifix_rotor_screw") {
      return "red";
    }
    if (type == "minifix_housing_screw") {
      return "green";
    } else {
      return "yellow";
    }
  }

  function Panel(props) {
    const component = props.component;
    const panel = props.panel;
    const allDrillings = props.allDrillings;

    const meshRef = useRef();

    // Subscribe this component to the render-loop
    // useFrame((state, delta) => (meshRef.current.rotation.x += delta))

    const panelDrilling =
      showDrilling && allDrillings
        ? props.allDrillings[panel.true_uniq_id]
        : false;

    return (
      <>
        <mesh
          position={[
            panel.position.x +
              panel.dimensions.width / 2 +
              component.position.x,
            panel.position.y +
              panel.dimensions.height / 2 +
              component.position.y,
            panel.position.z +
              panel.dimensions.depth / 2 +
              component.position.z,
          ]}
          ref={meshRef}
          castShadow={true}
          receiveShadow={true}
          key={panel.uniq_id}
        >
          <boxGeometry
            args={[
              panel.dimensions.width,
              panel.dimensions.height,
              panel.dimensions.depth,
            ]}
          />
          <LaminateMaterial panel={panel} />
        </mesh>
        {showEdges && (
          <lineSegments
            position={[
              panel.position.x +
                panel.dimensions.width / 2 +
                component.position.x,
              panel.position.y +
                panel.dimensions.height / 2 +
                component.position.y,
              panel.position.z +
                panel.dimensions.depth / 2 +
                component.position.z,
            ]}
          >
            <edgesGeometry
              args={[
                new THREE.BoxGeometry(
                  panel.dimensions.width,
                  panel.dimensions.height,
                  panel.dimensions.depth
                ),
              ]}
            />
            <lineBasicMaterial color="black" linewidth={1} />
          </lineSegments>
        )}

        {showDrilling &&
          panelDrilling &&
          panelDrilling
            .filter((drilling) => drilling.type != "groove")
            .map((drilling, drillingIndex) => {
              return (
                //   FIXME: Position should contain height/width offset for drilling size depending on orientation
                <mesh
                  rotation={getRotation(drilling)}
                  position={[
                    panel.position.x +
                      component.position.x +
                      getPosition(drilling, panel).x,
                    panel.position.y +
                      component.position.y +
                      getPosition(drilling, panel).y,
                    panel.position.z +
                      component.position.z +
                      getPosition(drilling, panel).z,
                  ]}
                >
                  <cylinderGeometry
                    args={[
                      drilling.diameter / 2,
                      drilling.diameter / 2,
                      drilling.depth,
                    ]}
                  ></cylinderGeometry>
                  <meshStandardMaterial
                    color={getDrillingColor(drilling.fixture_type)}
                  />
                </mesh>
              );
            })}
      </>
    );
  }

  function AmbientLight({ intensity }) {
    return <ambientLight intensity={intensity} />;
  }

  function renderComponent(component, allDrillings) {
    return component.panels
      .filter((panel) => !panel.is_removed || showRemovedShutters)
      .map((panel, panelIndex) => {
        return (
          <Panel
            panel={panel}
            component={component}
            allDrillings={allDrillings}
          ></Panel>
        );
      });
  }

  function renderPartition(partition, allDrillings) {
    return renderComponent(partition.component, allDrillings);
  }

  function renderShutter(shutter, allDrillings) {
    return renderComponent(shutter.component, allDrillings);
  }

  function renderSpace(space, allDrillings) {
    if (!space) return;
    let panels = [];
    if (space.component) {
      let clonedComponent = structuredClone(space.component);
      clonedComponent.position.x += space.abs_position.x;
      clonedComponent.position.y += space.abs_position.y;
      clonedComponent.position.z += space.abs_position.z;
      panels.push(renderComponent(clonedComponent, allDrillings));
    }
    (space.children || []).map(
      (childSpace, spaceIndex) =>
        (panels = [...panels, ...renderSpace(childSpace, allDrillings)])
    );
    return panels;
  }

  function Module(props) {
    const module = props.module;
    const useGroupRotation = props.useGroupRotation; // If true, don't apply individual rotation
    const relativePosition = props.relativePosition; // Optional relative position when in a group
    const groupRef = useRef();

    // Get rotation: use module rotation, or fallback to group rotation if module rotation is 0
    let rotation = module.rotation || 0;

    // Convert degrees to radians for Three.js (rotation is around Y-axis)
    // Negate rotation so 90° rotates left (right side appears)
    // Don't rotate if useGroupRotation is true (group will handle rotation)
    const rotationY =
      mode === "canvas" && !useGroupRotation ? (-rotation * Math.PI) / 180 : 0;

    const basePosition = relativePosition || module.position;
    let adjustedPosition = { ...basePosition };

    if (!module.group_id) {
      if (rotation === 90) {
        adjustedPosition.x = basePosition.x + module.dimensions.depth;
      } else if (rotation === 180) {
        adjustedPosition.x = basePosition.x + module.dimensions.width;
        adjustedPosition.z = basePosition.z + module.dimensions.depth;
      } else if (rotation === 270) {
        adjustedPosition.z = basePosition.z + module.dimensions.width;
      }
    }

    // Check if this module is selected
    const isSelected = !hideSelectionBorders && selectionManager.isSelected(module);

    // Calculate bounding box dimensions for selection border
    const getBoundingBox = () => {
      const width = module.dimensions.width;
      const height = module.dimensions.height;
      const depth = module.dimensions.depth;

      // Adjust for rotation
      let boxWidth = width;
      let boxDepth = depth;
      if ([90, 270].includes(rotation)) {
        boxWidth = depth;
        boxDepth = width;
      }

      return {
        width: boxWidth,
        height: height,
        depth: boxDepth,
        offsetX: boxWidth / 2,
        offsetY: height / 2,
        offsetZ: boxDepth / 2,
      };
    };

    const bbox = getBoundingBox();

    return (
      <group
        ref={groupRef}
        position={[adjustedPosition.x, adjustedPosition.y, adjustedPosition.z]}
        rotation={[0, rotationY, 0]}
        userData={{ type: "module", module: module }}
      >
        {renderComponent(module.template.base_component, module.drillings)}
        {module.template.partitions.map((partition, partitionIndex) =>
          renderPartition(partition, module.drillings)
        )}
        {renderSpace(module.template.root_space, module.drillings)}
        {showShutters &&
          module.template.shutters.map((shutter, shutterIndex) =>
            renderShutter(shutter, module.drillings)
          )}
        {/* Selection border for module (blue) - offset by half dimensions to align with module bounds */}
        {isSelected && (
          <lineSegments position={[bbox.offsetX, bbox.offsetY, bbox.offsetZ]}>
            <edgesGeometry
              args={[
                new THREE.BoxGeometry(bbox.width, bbox.height, bbox.depth),
              ]}
            />
            <lineBasicMaterial color="#0000CC" linewidth={3} />
          </lineSegments>
        )}
      </group>
    );
  }

  function Group(props) {
    const group = props.group;
    const groupRef = useRef();

    // Get group rotation
    // Negate rotation so 90° rotates left (right side appears)
    const rotation = group.rotation || 0;
    const rotationY = mode === "canvas" ? (-rotation * Math.PI) / 180 : 0;
    const basePosition = group.position;
    let adjustedPosition = { ...basePosition };

    if (rotation === 90) {
      adjustedPosition.x = basePosition.x + group.dimensions.depth;
    } else if (rotation === 180) {
      adjustedPosition.x = basePosition.x + group.dimensions.width;
      adjustedPosition.z = basePosition.z + group.dimensions.depth;
    } else if (rotation === 270) {
      adjustedPosition.z = basePosition.z + group.dimensions.width;
    }

    // Check if this group is selected
    const isSelected = !hideSelectionBorders && selectionManager.isSelected(group);

    // Calculate bounding box dimensions for selection border
    const getBoundingBox = () => {
      const width = group.dimensions.width;
      const height = group.dimensions.height;
      const depth = group.dimensions.depth;

      // Adjust for rotation
      let boxWidth = width;
      let boxDepth = depth;
      if ([90, 270].includes(rotation)) {
        boxWidth = depth;
        boxDepth = width;
      }

      return {
        width: boxWidth,
        height: height,
        depth: boxDepth,
        offsetX: boxWidth / 2,
        offsetY: height / 2,
        offsetZ: boxDepth / 2,
      };
    };

    const bbox = getBoundingBox();

    return (
      <group
        ref={groupRef}
        position={[adjustedPosition.x, adjustedPosition.y, adjustedPosition.z]}
        rotation={[0, rotationY, 0]}
        userData={{ type: "group", group: group }}
      >
        {group.modules
          .filter((module) => module.template)
          .map((module, module_index) => {
            // Calculate module position relative to group
            const relativePosition = {
              x: module.position.x - group.position.x,
              y: module.position.y - group.position.y,
              z: (module.position.z || 0) - (group.position.z || 0),
            };

            return (
              <Module
                key={module_index}
                module={module}
                relativePosition={relativePosition}
                useGroupRotation={true}
              />
            );
          })}
        {/* Selection border for group (red) - offset by half dimensions to align with group bounds */}
        {isSelected && (
          <lineSegments position={[bbox.offsetX, bbox.offsetY, bbox.offsetZ]}>
            <edgesGeometry
              args={[
                new THREE.BoxGeometry(bbox.width, bbox.height, bbox.depth),
              ]}
            />
            <lineBasicMaterial color="#CC0000" linewidth={3} />
          </lineSegments>
        )}
      </group>
    );
  }

  // Collect all modules (standalone and from groups) for camera positioning
  let allModules = currentRoom.modules;
  currentRoom.groups.forEach((group) => {
    allModules = [...allModules, ...group.modules];
  });

  function Room() {
    if (mode === "canvas") {
      return (
        <>
          {/* Render standalone modules */}
          {currentRoom.modules
            .filter((module) => !module.group_id && module.template)
            .map((module, module_index) => (
              <Module key={module_index} module={module} />
            ))}
          {/* Render groups with their modules */}
          {currentRoom.groups
            .filter((group) => group.modules && group.modules.length > 0)
            .map((group, group_index) => (
              <Group key={group_index} group={group} />
            ))}
        </>
      );
    } else {
      let ids = [];
      const activeModule = selectionManager.activeModule();
      const activeGroup =
        selectionManager.activeGroup() &&
        selectionManager.findGroup(selectionManager.activeGroup().id);

      if (activeGroup) ids = activeGroup.modules.map((e) => e.id);
      else if (activeModule) ids = [activeModule.id];

      return allModules
        .filter((module) => ids.includes(module.id))
        .filter((module) => module.template)
        .map((module, module_index) => (
          <Module key={module_index} module={module} />
        ));
    }
  }

  let camera = new THREE.PerspectiveCamera(90, 1, 0.1, 1000000);

  const [savedCameraState, setSavedCameraState] = useState(null);
  const [hideSelectionBorders, setHideSelectionBorders] = useState(false);

  function Scene() {
    const cameraControlsRef = useRef();
    const { raycaster, mouse, gl, scene: threeScene } = useThree();
    const [, forceUpdate] = useState(0);
    const selectionKeyRef = useRef(selectionManager.getKey ? selectionManager.getKey() : JSON.stringify(selectionManager.selections()));

    // Track selection changes to force re-render - optimized to prevent flicker
    const isUpdatingRef = useRef(false);
    useEffect(() => {
      const updateSelection = () => {
        if (isUpdatingRef.current) return; // Prevent concurrent updates

        const currentKey = selectionManager.getKey ? selectionManager.getKey() : JSON.stringify(selectionManager.selections());
        if (currentKey !== selectionKeyRef.current) {
          isUpdatingRef.current = true;
          selectionKeyRef.current = currentKey;
          setHideSelectionBorders(false);

          // Use requestAnimationFrame to batch updates and prevent flicker
          requestAnimationFrame(() => {
            forceUpdate(prev => prev + 1);
            isUpdatingRef.current = false;
          });
        }
      };

      // Check for selection changes less frequently to reduce flicker
      const interval = setInterval(updateSelection, 300);
      return () => clearInterval(interval);
    }, [selectionManager]);

    // Handle click events - only prevent camera controls on actual clicks, not drags
    useEffect(() => {
      let clickStartTime = null;
      let clickStartPos = null;
      const CLICK_THRESHOLD = 5; // pixels
      const CLICK_TIME_THRESHOLD = 200; // ms

      const handleClick = (event) => {
        if (mode !== "canvas") return;

        // Check if this was a click (not a drag)
        const isClick = clickStartTime &&
          (Date.now() - clickStartTime < CLICK_TIME_THRESHOLD) &&
          clickStartPos &&
          (Math.abs(event.clientX - clickStartPos.x) < CLICK_THRESHOLD &&
           Math.abs(event.clientY - clickStartPos.y) < CLICK_THRESHOLD);

        if (!isClick) {
          clickStartTime = null;
          clickStartPos = null;
          return;
        }

        // Reset click tracking
        clickStartTime = null;
        clickStartPos = null;

        // Calculate mouse position in normalized device coordinates
        const rect = gl.domElement.getBoundingClientRect();
        const mouseX = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        const mouseY = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Update raycaster
        raycaster.setFromCamera({ x: mouseX, y: mouseY }, camera);

        // Find all intersecting objects in the scene
        const intersects = raycaster.intersectObjects(threeScene.children, true);

        if (intersects.length > 0) {
          // Find the first object with userData containing module or group
          let clickedObject = null;
          for (const intersect of intersects) {
            let obj = intersect.object;
            // Traverse up the parent chain to find group with userData
            while (obj) {
              if (obj.userData && (obj.userData.module || obj.userData.group)) {
                clickedObject = obj;
                break;
              }
              obj = obj.parent;
            }
            if (clickedObject) break;
          }

          if (clickedObject && clickedObject.userData) {
            const userData = clickedObject.userData;

            if (userData.module) {
              // Clicked on a module
              const module = userData.module;

              // Check if module is inside a group
              if (module.group_id) {
                const group = selectionManager.findGroup(module.group_id);

                if (group) {
                  // Check if this is a double click - directly select the module
                  if (event.detail === 2) {
                    // Double click: select the module directly
                    if (event.shiftKey) {
                      selectionManager.toggle(module, true);
                    } else {
                      selectionManager.select(module);
                    }
                  } else {
                    // Single click: check if group is already selected
                    const isGroupSelected = selectionManager.isSelected(group);

                    if (isGroupSelected) {
                      // Group is already selected, now select the module
                      if (event.shiftKey) {
                        selectionManager.toggle(module, true);
                      } else {
                        selectionManager.select(module);
                      }
                    } else {
                      // Group is not selected, select the group first
                      if (event.shiftKey) {
                        selectionManager.toggle(group, true);
                      } else {
                        selectionManager.select(group);
                      }
                    }
                  }
                } else {
                  // Group not found, select module directly
                  selectionManager.toggle(module, event.shiftKey);
                }
              } else {
                // Standalone module (not in a group), select directly
                selectionManager.toggle(module, event.shiftKey);
              }
            } else if (userData.group) {
              // Clicked directly on a group
              const group = userData.group;
              selectionManager.toggle(group, event.shiftKey);
            }

            // Only stop propagation if we clicked on an object (prevents camera from moving)
            event.stopPropagation();
          } else if (!event.shiftKey) {
            // Clicked on empty space - clear selection
            selectionManager.clear();
          }
        } else if (!event.shiftKey) {
          // Clicked on empty space - clear selection
          selectionManager.clear();
        }
      };

      const handleMouseDown = (event) => {
        if (mode !== "canvas") return;
        if (event.button === 0) { // Left mouse button
          clickStartTime = Date.now();
          clickStartPos = { x: event.clientX, y: event.clientY };
        }
      };

      gl.domElement.addEventListener("mousedown", handleMouseDown);
      gl.domElement.addEventListener("click", handleClick, true);
      return () => {
        gl.domElement.removeEventListener("mousedown", handleMouseDown);
        gl.domElement.removeEventListener("click", handleClick, true);
      };
    }, [mode, raycaster, gl, camera, selectionManager, threeScene]);

    // Only set initial camera position, don't move camera on selection changes
    useEffect(() => {
      if (!cameraControlsRef.current) return;

      if (savedCameraState) {
        // Restore saved camera position and target
        cameraControlsRef.current.setLookAt(
          savedCameraState.position.x,
          savedCameraState.position.y,
          savedCameraState.position.z,
          savedCameraState.target.x,
          savedCameraState.target.y,
          savedCameraState.target.z,
          false
        );
      } else {
        // Only set initial camera position on first load
        let module = null;

        if (mode === "canvas") module = allModules[0];
        else {
          const activeModule = selectionManager.activeModule();
          const activeGroup = selectionManager.activeGroup();

          if (activeModule) {
            if (activeModule.is_packed) module = activeGroup.modules[0];
            else module = activeModule;
          } else if (activeGroup) module = activeGroup.modules[0];
        }

        if (!module) return;

        const { position, dimensions } = module;

        cameraControlsRef.current.setLookAt(
          position.x + dimensions.width / 2,
          position.y + dimensions.height / 2,
          (position.z || 0) + dimensions.depth + 3000,
          position.x + dimensions.width / 2,
          position.y + dimensions.height / 2,
          0,
          false
        );
      }
    }, []); // Only run once on mount

    // Keyboard shortcut: snap camera to "left view" of selected module
    useEffect(() => {
      const handleKeyDown = (e) => {
        if (!(e.shiftKey && (e.key === "L" || e.key === "l"))) return;
        e.preventDefault();
        e.stopPropagation?.();

        if (!cameraControlsRef.current) {
          alert("3D camera controls not ready yet.");
          return;
        }

        const selections =
          typeof selectionManager.selections === "function"
            ? selectionManager.selections()
            : [];
        const selectedModules = selections.filter((s) => s?.type === "module");

        if (selectedModules.length !== 1 || selections.length !== 1) {
          alert(
            `Shift+L requires exactly one selected module. (selected: ${selections.length})`
          );
          return;
        }

        const selected = selectedModules[0];
        const module =
          typeof selectionManager.findModule === "function"
            ? selectionManager.findModule(selected.id)
            : selected;

        if (!module?.dimensions || !module?.position) {
          alert("Selected module is missing position/dimensions.");
          return;
        }

        const { dimensions } = module;
        const height = dimensions.height || 0;
        const depth = dimensions.depth || 0;
        const width = dimensions.width || 0;
        const BASE_OFFSET = 2500;
        const LEFT_VIEW_FOV = 30;

        let moduleObject = null;
        threeScene.traverse((obj) => {
          if (moduleObject) return;
          if (obj?.userData?.module?.id === module.id) moduleObject = obj;
        });
        if (!moduleObject) {
          alert("Could not find selected module in 3D scene.");
          return;
        }

        threeScene.updateMatrixWorld(true);
        moduleObject.updateWorldMatrix(true, true);

        // Backend left-view formula in local coords:
        // eye    = { x: -width/2 - 2.5, y: height/2, z: depth + 2.5 }
        // lookAt = { x:  width/2,       y: height/2, z: depth }
        //
        // We compute the target with localToWorld, but compute the eye by
        // moving from the target along the module's *world rotation basis*.
        // This avoids left/right flipping when the hierarchy has mirrored scale.
        const localTarget = new THREE.Vector3(width / 2, height / 2, depth);
        const worldTarget = moduleObject.localToWorld(localTarget.clone());

        const q = new THREE.Quaternion();
        moduleObject.getWorldQuaternion(q);
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(q);
        const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(q);

        const worldEye = worldTarget
          .clone()
          .addScaledVector(right, -(width / 2 + BASE_OFFSET))
          .addScaledVector(forward, BASE_OFFSET);

        const targetX = worldTarget.x;
        const targetY = worldTarget.y;
        const targetZ = worldTarget.z;
        const eyeX = worldEye.x;
        const eyeY = worldEye.y;
        const eyeZ = worldEye.z;

        if (
          !Number.isFinite(eyeX) ||
          !Number.isFinite(eyeY) ||
          !Number.isFinite(eyeZ) ||
          !Number.isFinite(targetX) ||
          !Number.isFinite(targetY) ||
          !Number.isFinite(targetZ)
        ) {
          alert("Computed camera values are invalid (NaN/Infinity).");
          return;
        }

        if (camera?.isPerspectiveCamera) {
          const cameraTargetDistance = Math.hypot(
            eyeX - targetX,
            eyeY - targetY,
            eyeZ - targetZ
          );
          const boundsSize = Math.max(1, Math.hypot(width, height, depth));
          camera.fov = LEFT_VIEW_FOV;
          camera.near = Math.max(10, cameraTargetDistance - boundsSize * 2);
          camera.far = Math.max(
            camera.near + 1000,
            cameraTargetDistance + boundsSize * 4 + BASE_OFFSET
          );
          camera.updateProjectionMatrix();
        }

        cameraControlsRef.current.setLookAt(
          eyeX,
          eyeY,
          eyeZ,
          targetX,
          targetY,
          targetZ,
          false
        );
        setHideSelectionBorders(true);
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [selectionManager, threeScene]);

    // Keyboard shortcut: export all room modules as individual GLBs in ZIP
    useEffect(() => {
      const handleKeyDown = async (e) => {
        if (!(e.shiftKey && (e.key === "G" || e.key === "g"))) return;
        e.preventDefault();
        e.stopPropagation?.();
        try {
          const { zipBlob, exportedCount, skippedModuleIds } =
            await exportRoomModulesAsGlbZip({ currentRoom, threeScene });
          if (skippedModuleIds.length > 0) {
            alert(
              `Exporting ${exportedCount} modules. ${skippedModuleIds.length} module(s) were not found in the 3D scene and were skipped.`
            );
          }
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement("a");
          a.href = url;
          a.download = buildRoomModulesZipName(currentRoom);
          document.body.appendChild(a);
          a.click();
          a.remove();
          URL.revokeObjectURL(url);
        } catch (error) {
          alert(`Failed to export module ZIP: ${error?.message || error}`);
        }
      };

      window.addEventListener("keydown", handleKeyDown);
      return () => window.removeEventListener("keydown", handleKeyDown);
    }, [currentRoom, threeScene]);

    // Save camera state on unmount
    useEffect(() => {
      return () => {
        if (cameraControlsRef.current) {
          const currentPos = camera.position;
          if (currentPos) {
            const target = cameraControlsRef.current.getTarget();
            setSavedCameraState({
              position: { x: currentPos.x, y: currentPos.y, z: currentPos.z },
              target: { x: target.x, y: target.y, z: target.z },
            });
          }
        }
      };
    }, []);

    return (
      <>
        <CameraControls
          ref={cameraControlsRef}
          minDistance={0.1}
          enabled={true}
          verticalDragToForward={false}
          dollyToCursor={true}
          infinityDolly={true}
        />
        {AmbientLight({ intensity: Math.PI / 2 })}
        <Room />
        <pointLight
          position={[2000, 200, 1600]}
          shadows={true}
          decay={0}
          intensity={Math.PI}
        />
      </>
    );
  }

  return didCatch ? (
    <div>{error.message}</div>
  ) : (
    <div className={"fh flex-grow-1 position-relative"}>
      <ErrorBoundary>
        <Canvas camera={camera} fallback={<div>Sorry no WebGL supported!</div>}>
          <Scene></Scene>
        </Canvas>
      </ErrorBoundary>
    </div>
  );
}

export default ThreeDCanvas;
